import { useEffect, useLayoutEffect, useRef, useState } from "react";

// The preview column is only ~650px wide: well under Tailwind's 1024px
// `lg:` breakpoint, so an iframe simply filling that column would make the
// real header render its *mobile* nav even in "Desktop" mode (the exact
// invisible-desktop-nav bug this constant fixes). Desktop mode instead
// renders the iframe at a genuine desktop width and scales it down to fit,
// the same technique device-preview tools use, so `lg:` breakpoints inside
// actually see a real desktop viewport.
const NATIVE_DESKTOP_WIDTH = 1120;
const NATIVE_DESKTOP_HEIGHT = 640;

/**
 * Both Desktop and Phone modes render through this SAME iframe-based
 * mechanism, pointed at /embed/renter-walkthrough, never a shrunk-down or
 * stretched div. Tailwind's `sm:`/`md:`/`lg:` classes are real *browser
 * viewport* media queries, not container queries: putting the real
 * components inline in the actual page and just narrowing their wrapping
 * div does NOT make them lay out at that width: the header's desktop nav,
 * RentalProfile's 3-column grid etc. still read the real (often 1280px+)
 * browser window and render their full-width desktop layout regardless of
 * how narrow the box around them is, spilling hundreds of pixels past its
 * edge. An iframe genuinely is its own viewport, so whatever width it's
 * given is what the real breakpoints inside it actually see: Desktop mode
 * gets a real iframe sized to fill the preview column (honestly showing
 * whatever this real site's own breakpoints render at that width), Phone
 * mode gets a real iframe fixed at a real phone width.
 *
 * State ownership stays entirely in the parent (ProductWalkthrough): this
 * component just mirrors {stepIndex, demoState, reducedMotion} into the
 * iframe via postMessage on every change, and relays events the iframe
 * posts back up through `onEvent`, so Desktop ⇄ Phone always shows the same
 * walkthrough state.
 */
export default function DeviceFrame({ mode, stepIndex, demoState, reducedMotion, onEvent, onSubstep, resetToken, src = "/embed/renter-walkthrough", testIdBase = "walkthrough" }) {
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
  }, [mode]);

  useEffect(() => {
    function onMessage(e) {
      if (e.origin !== window.location.origin) return;
      const data = e.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "WALKTHROUGH_FRAME_READY") { setReady(true); return; }
      if (data.type === "WALKTHROUGH_EVENT") { onEvent?.(data.event); return; }
      // The left-side explanation panel (in the parent) needs to track the
      // current internal substep, not just which outer step is active: see
      // RenterWalkthroughStage's resolveSubstepCopy for what this carries.
      if (data.type === "WALKTHROUGH_SUBSTEP") { onSubstep?.(data.stepKey, data.substep); return; }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onEvent, onSubstep]);

  useEffect(() => {
    if (!ready || !iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      { type: "WALKTHROUGH_STATE", stepIndex, demoState, deviceMode: mode, reducedMotion, resetToken },
      window.location.origin
    );
  }, [ready, stepIndex, demoState, reducedMotion, mode, resetToken]);

  const iframeEl = (
    <iframe
      ref={iframeRef}
      title={`ConfirmPH walkthrough: ${mode} preview`}
      src={src}
      className="w-full h-full border-0 bg-white"
      data-testid={`${testIdBase}-${mode}-iframe`}
      onLoad={() => {
        // The child announces WALKTHROUGH_FRAME_READY itself once mounted;
        // this onLoad is a fallback in case that message races the load event.
        iframeRef.current?.contentWindow?.postMessage({ type: "WALKTHROUGH_PING" }, window.location.origin);
      }}
    />
  );

  if (mode === "phone") {
    return (
      <div className="mx-auto rounded-[2rem] border-[6px] border-slate-900 bg-slate-900 shadow-xl w-full max-w-[380px]" data-testid={`${testIdBase}-phone-frame`}>
        <div className="flex items-center justify-between px-4 pt-2 pb-1 text-[10px] text-white">
          <span className="font-mono">9:41</span>
          <span className="font-mono opacity-80">DEMO · ••• 5G ▄▅▇</span>
        </div>
        <div className="bg-white rounded-t-2xl overflow-hidden" style={{ height: 640, width: 353, margin: "0 auto" }}>
          {iframeEl}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`${testIdBase}-desktop-frame`}>
      <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 border-b border-slate-200">
        <span className="w-2 h-2 rounded-full bg-rose-300" />
        <span className="w-2 h-2 rounded-full bg-amber-300" />
        <span className="w-2 h-2 rounded-full bg-emerald-300" />
        <span className="ml-2 text-[10px] font-mono text-slate-500">confirmph.app</span>
      </div>
      <DesktopScaledViewport testIdBase={testIdBase}>{iframeEl}</DesktopScaledViewport>
    </div>
  );
}

/**
 * Renders its child at NATIVE_DESKTOP_WIDTH (a real desktop viewport, so the
 * real header's `lg:` breakpoint actually applies) inside a CSS-scaled
 * wrapper sized to whatever width the preview column actually has. Clicks
 * land correctly through a CSS transform: this is a purely visual scale,
 * not a layout resize, so the browser translates pointer coordinates for us.
 */
function DesktopScaledViewport({ children, testIdBase = "walkthrough" }) {
  const outerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    function measure() {
      const width = el.getBoundingClientRect().width;
      if (width > 0) setScale(width / NATIVE_DESKTOP_WIDTH);
    }
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <div ref={outerRef} style={{ height: NATIVE_DESKTOP_HEIGHT * scale, overflow: "hidden" }} data-testid={`${testIdBase}-desktop-scaled-viewport`}>
      <div style={{ width: NATIVE_DESKTOP_WIDTH, height: NATIVE_DESKTOP_HEIGHT, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        {children}
      </div>
    </div>
  );
}
